import { useEffect, useRef, useState } from 'react';

/**
 * 켜진 상태를 최소 시간만큼 유지한다.
 *
 * 세션 확인이 200ms 만에 끝나면 스피너가 번쩍이고 사라진다.
 * 무언가 지나갔다는 것만 남고 무엇이었는지는 못 읽는다.
 * 그럴 바에는 아예 안 보이는 편이 낫고, 보여줄 것이라면 읽을 시간을 줘야 한다.
 *
 * 🔴 지연을 더하는 것이 아니라 하한을 두는 것이다
 *   요청이 이미 900ms 걸렸다면 추가로 기다리지 않는다.
 *   무조건 900ms 를 더하면 느린 네트워크에서 두 배로 느려진다.
 *
 * 🔴 앱을 처음 열 때만 걸린다
 *   호출부가 useMe 의 isLoading 인데, 그 쿼리는 한 번 받아오면 캐시된다.
 *   화면을 옮길 때마다 900ms 씩 기다리게 되는 것이 아니다.
 *
 * @param active   지금 켜져 있어야 하는가 (예: 로딩 중)
 * @param minimumMs 켜진 뒤 최소한 이만큼은 유지한다
 * @returns 화면에 보여줄 상태
 */
export function useMinimumDuration(active: boolean, minimumMs: number): boolean {
  const [visible, setVisible] = useState(active);

  /*
    켜진 시각. state 가 아니라 ref 인 이유는 이 값이 바뀌어도
    다시 그릴 필요가 없기 때문이다. state 로 두면 렌더가 한 번 더 돈다.
  */
  const startedAt = useRef<number | null>(active ? Date.now() : null);

  useEffect(() => {
    if (active) {
      startedAt.current ??= Date.now();
      setVisible(true);
      return;
    }

    // 켜진 적이 없으면 기다릴 것도 없다.
    if (startedAt.current === null) {
      setVisible(false);
      return;
    }

    const 남은시간 = minimumMs - (Date.now() - startedAt.current);

    if (남은시간 <= 0) {
      startedAt.current = null;
      setVisible(false);
      return;
    }

    const timer = setTimeout(() => {
      startedAt.current = null;
      setVisible(false);
    }, 남은시간);

    return () => clearTimeout(timer);
  }, [active, minimumMs]);

  return visible;
}
